import { useState, useEffect, useCallback } from 'react';
import { Referral, ReferralStatus } from '@/utils/referral/types';

// Mock data generator
const generateMockReferrals = (count: number): Referral[] => {
  return Array(count)
    .fill(null)
    .map((_, index) => ({
      id: `ref-${index}`,
      referrerId: `user-${100 + index % 3}`,
      referrerName: `Referrer ${index % 3}`,
      referrerEmail: `referrer${index % 3}@example.com`,
      refereeId: `user-${200 + index}`,
      refereeName: `Referee ${index}`,
      refereeEmail: `referee${index}@example.com`,
      status: index % 4 === 0 
        ? ReferralStatus.COMPLETED 
        : index % 4 === 1 
          ? ReferralStatus.PENDING 
          : index % 4 === 2 
            ? ReferralStatus.CANCELLED 
            : ReferralStatus.EXPIRED, // Now using EXPIRED from ReferralStatus
      points: 100 + (index * 10) % 50,
      createdAt: new Date(Date.now() - index * 86400000).toISOString(),
      convertedAt: index % 4 === 0 ? new Date(Date.now() - index * 43200000).toISOString() : undefined,
      name: `Referee ${index}`,
      email: `referee${index}@example.com`,
      date: new Date(Date.now() - index * 86400000).toISOString()
    }));
};

export const useReferralUsers = () => {
  const [users, setUsers] = useState<Referral[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<Referral[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  
  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would be an API call
        // Simulating API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const mockUsers = generateMockReferrals(20);
        setUsers(mockUsers);
        setFilteredUsers(mockUsers);
      } catch (error) {
        console.error('Error fetching referral users:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsers();
  }, []);
  
  // Filter users when search term or status filter changes
  useEffect(() => {
    let result = [...users];
    
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        user => 
          user.refereeName?.toLowerCase().includes(lowerSearch) || 
          user.refereeEmail?.toLowerCase().includes(lowerSearch) ||
          user.referrerName?.toLowerCase().includes(lowerSearch) ||
          user.referrerEmail?.toLowerCase().includes(lowerSearch)
      );
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(user => user.status === statusFilter);
    }
    
    setFilteredUsers(result);
  }, [users, searchTerm, statusFilter]);
  
  const approveUser = useCallback((userId: string) => {
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === userId 
          ? { ...user, status: ReferralStatus.APPROVED } 
          : user
      )
    );
    
    // In a real app, this would make an API call
    console.log(`Approving user ${userId}`);
  }, []);
  
  const rejectUser = useCallback((userId: string) => {
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === userId 
          ? { ...user, status: ReferralStatus.REJECTED } 
          : user
      )
    );
    
    // In a real app, this would make an API call
    console.log(`Rejecting user ${userId}`);
  }, []);
  
  const deleteUser = useCallback((userId: string) => {
    // In a real app, this would make an API call before removing from state
    console.log(`Deleting user ${userId}`);
    
    setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
  }, []);
  
  return {
    users: filteredUsers,
    isLoading,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    approveUser,
    rejectUser,
    deleteUser
  };
};
